import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from '@chakra-ui/modal'
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Icon,
    Input,
    Link,
    Text,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { CgRename } from 'react-icons/cg'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { useHistory } from 'react-router-dom'
import { colors } from '../../../../ui-kit'
import useToast from '../../../hooks/useToast'
import {
    MediaLibrarySearchFolderResponse,
    RenameFolderErrorResponse,
    RenameFolderSuccessfulResponse,
    BulkJobItem, OperationStatus
} from '../../../interfaces'
import { MLContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import { Base64 } from 'js-base64'
import { useGetQueryParams } from '../../../hooks/useGetQueryParams'
import { addLeadingSlash, removeTrailingSlash } from '../../../../utils/urlHelpers'

interface Props {
    isOpen: boolean
    onClose(): void
}

interface FormProps {
    newFolderName: string
    purgeCache: boolean
}

const RenameFolderModal = ({ isOpen, onClose }: Props): React.ReactElement => {
    const { t } = useTranslation()
    const toast = useToast()
    const [selectedFolder, setSelectedFolder] =
        useState<MediaLibrarySearchFolderResponse>()
    const [formError, setFormError] = useState<string>('')
    const { response, request } = useFetch({
        cachePolicy: CachePolicies.NO_CACHE,
    })
    const [{ selectedFolders, selectedBreadcrumbFolder, folderPath, refetchFolders }, dispatch] = useContext(MLContext)

    useEffect(() => {
        if (selectedFolders.length > 0) {
            setSelectedFolder(selectedFolders[0]!)
        }
    }, [isOpen])

    const history = useHistory()
    const queryFromUrl = useGetQueryParams()

    const isBreadcrumbFolderRenamed = useMemo(() => {
        if (selectedBreadcrumbFolder && selectedFolders.length > 0) {
            return selectedBreadcrumbFolder.folderId === selectedFolders[0]!.folderId
        }

        return false
    }, [selectedBreadcrumbFolder, selectedFolders])

    const replaceUrlPath = useCallback((newName: string) => {
        if (isBreadcrumbFolderRenamed && selectedBreadcrumbFolder?.name) {
            let newFolderPath = selectedBreadcrumbFolder.folderPath
            if (folderPath === '/') {
                newFolderPath = `/${newName}`
            } else {
                const newFolderPathArray: string[] = addLeadingSlash(removeTrailingSlash(folderPath)).split('/');
                newFolderPathArray.pop()
                newFolderPathArray.push(newName)
                newFolderPath = addLeadingSlash(newFolderPathArray.join('/'))
            }
            history.replace(
                `/media-library/${Base64.encode(newFolderPath, true)}${queryFromUrl as string
                }`,
            )
        }
    }, [selectedBreadcrumbFolder, folderPath, isBreadcrumbFolderRenamed, queryFromUrl, history])

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                onClose()
                setFormError('')
            }}
            size="lg"
        >
            <ModalOverlay />
            <ModalContent>
                <Formik<FormProps>
                    enableReinitialize
                    initialValues={{
                        newFolderName: selectedFolder?.name ?? '',
                        purgeCache: false,
                    }}
                    onSubmit={async (values): Promise<void> => {
                        setFormError('')

                        const item: BulkJobItem[] = [
                            ...selectedFolders.map(folder => ({
                                id: folder.folderId,
                                name: folder.name,
                                type: 'folder',
                                status: OperationStatus.INDETERMINATE,
                            }))] as BulkJobItem[]
                        const res = (await request.post('/api/v2/bulkJobs/renameFolder', {
                            folderPath: selectedFolder?.folderPath,
                            newFolderName: values.newFolderName,
                            purgeCache: values.purgeCache,
                        })) as
                            RenameFolderErrorResponse
                            | RenameFolderSuccessfulResponse

                        if (request.error) {
                            if ([400, 401, 403, 404, 409, 423].includes(response.status)) {
                                setFormError((res as RenameFolderErrorResponse).message)
                            } else {
                                setFormError(
                                    t(
                                        'error_in_renaming_folder',
                                        'Error in renaming folder, please try again after sometime.',
                                    ),
                                )
                            }

                            item[0].status = OperationStatus.ERROR
                            item[0].errorMessage = (res as RenameFolderErrorResponse).message
                            dispatch({
                                type: MediaLibraryActions.SET_RENAME_ITEM,
                                payload: item,
                            })

                        } else if (response.status === 202) {
                            toast({
                                description: t('folder_rename_progress', 'Folder rename in progress'),
                                status: 'info',
                                isClosable: true,
                                duration: 5000,
                                position: 'top-right',
                                variant: 'outline',
                            })
                            setFormError('')
                            onClose()
                            if (isBreadcrumbFolderRenamed) {
                                replaceUrlPath(values.newFolderName)
                            }
                            void refetchFolders()

                            item[0].status = OperationStatus.PENDING
                            item[0].jobId = (res as RenameFolderSuccessfulResponse).jobId
                            dispatch({
                                type: MediaLibraryActions.SET_RENAME_ITEM,
                                payload: item,
                            })
                        }
                    }}
                    validationSchema={Yup.object().shape({
                        newFolderName: Yup.string().required('Required'),
                        purgeCache: Yup.boolean().required('Required'),
                    })}
                >
                    {({ values, handleChange, isSubmitting, submitForm }) => (
                        <>
                            <ModalHeader>
                                <Icon
                                    as={CgRename}
                                    color="appBattleshipGrey"
                                    backgroundColor="appPaleGrey"
                                    boxSize={6}
                                    padding="0.1em"
                                    borderRadius="full"
                                    marginRight="0.5em"
                                />
                                {t('rename_folder', 'Rename Folder')}
                            </ModalHeader>
                            <ModalCloseButton />
                            <ModalBody>
                                <Alert status="info">
                                    <AlertIcon />
                                    <AlertDescription>
                                        <Trans t={t} i18nKey="rename_file_purge_cache_alert">
                                            <Text lineHeight="1.2em">
                                                Purge requests are counted against account's
                                            </Text>{' '}
                                            <Link
                                                lineHeight="1.2em"
                                                href="https://docs.imagekit.io/api-reference/media-api/purge-cache#limitations"
                                                isExternal
                                            >
                                                purge API limits
                                            </Link>
                                        </Trans>
                                    </AlertDescription>
                                </Alert>
                                {formError ? (
                                    <Alert status="error" mt="1em">
                                        <AlertIcon />
                                        <AlertDescription>{formError}</AlertDescription>
                                    </Alert>
                                ) : null}
                                <Field name="newFolderName">
                                    {({ field, form }: FieldProps<string, FormProps>) => (
                                        <FormControl
                                            marginTop="1em"
                                            isInvalid={Boolean(
                                                form.errors.newFolderName && form.touched.newFolderName,
                                            )}
                                            isRequired
                                        >
                                            <FormLabel>
                                                {t('new_folder_name', 'New Folder Name')}
                                            </FormLabel>
                                            <Trans t={t} i18nKey="rename_folder_field_description">
                                                <Text color="appBattleshipGrey" marginBottom="0.5em">
                                                    Enter a valid folder name.{' '}
                                                    <Link
                                                        href="https://docs.imagekit.io/api-reference/media-api/rename-folder"
                                                        lineHeight="1.15em"
                                                        isExternal
                                                    >
                                                        Learn More.
                                                    </Link>
                                                </Text>
                                            </Trans>
                                            <Input {...field} placeholder={selectedFolder?.name} />
                                            {form.errors.newFolderName && form.touched.newFolderName ? (
                                                <FormErrorMessage mt="0.25em">
                                                    {form.errors.newFolderName}
                                                </FormErrorMessage>
                                            ) : (
                                                <Box height="1.55em" />
                                            )}
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name="purgeCache">
                                    {({ field, form }: FieldProps<string, FormProps>) => (
                                        <FormControl
                                            marginTop="0.5em"
                                            isInvalid={Boolean(
                                                form.errors.purgeCache && form.touched.purgeCache,
                                            )}
                                            isRequired
                                        >
                                            <Checkbox
                                                {...field}
                                                id="purgeCache"
                                                isInvalid={Boolean(
                                                    form.errors.purgeCache && form.touched.purgeCache,
                                                )}
                                                isChecked={values.purgeCache}
                                                onChange={handleChange}
                                            >
                                                {t(
                                                    'purge_nested_files_cache_checkbox_label',
                                                    'Purge CDN cache for nested files.',
                                                )}
                                            </Checkbox>
                                            {form.errors.purgeCache && form.touched.purgeCache ? (
                                                <FormErrorMessage mt="0.25em">
                                                    {form.errors.purgeCache}
                                                </FormErrorMessage>
                                            ) : (
                                                <Box height="1.55em" />
                                            )}
                                        </FormControl>
                                    )}
                                </Field>
                            </ModalBody>
                            <ModalFooter
                                borderTop={`1px solid ${colors.appGrey}`}
                                style={{ gap: '1em' }}
                            >
                                <Button
                                    onClick={() => {
                                        onClose()
                                        setFormError('')
                                    }}
                                    variant="outline"
                                >
                                    {t('cancel', 'Cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    isDisabled={isSubmitting}
                                    isLoading={isSubmitting}
                                    onClick={async () => {
                                        await submitForm()
                                        dispatch({
                                            type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                                        })
                                    }}
                                >
                                    {t('submit', 'Submit')}
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </Formik>
            </ModalContent>
        </Modal>
    )
}

export default RenameFolderModal
